/**
 * Avaliacao da logica condicional dos campos (branching).
 *
 * Funcoes puras (sem efeito colateral) — usadas pelo FormRenderer (preview do
 * builder + pagina publica) e por testes.
 *
 * A regra e' a MESMA de `formularios.services.campo_visivel` /
 * `campos_visiveis` no backend (FORM-08): quando divergiam, o visitante via um
 * campo oculto e o servidor exigia o obrigatorio dele. A tabela de casos de
 * `condicoes.test.js` e' identica a de `formularios/tests/test_condicoes.py`.
 *
 * Shape de `condicao` (vazio/ausente = sempre visivel):
 *   {
 *     acao: "MOSTRAR" | "OCULTAR",
 *     modo: "TODAS" | "QUALQUER",
 *     regras: [{ campo: <id ou _key>, operador, valor }]
 *   }
 *
 * Operadores: IGUAL, DIFERENTE, CONTEM, PREENCHIDO, VAZIO. Comparacao sem caixa
 * e sem espacos nas pontas (NFC + trim + minusculas); em lista (multipla
 * escolha), IGUAL e CONTEM batem com algum item igual e DIFERENTE com nenhum.
 */

/**
 * Identificador do campo nas regras e no estado das respostas.
 *
 * No Editor os campos novos ainda nao tem id e as regras citam a `_key`; na
 * pagina publica o campo nao tem `_key` e as regras citam o UUID.
 */
export function chaveDoCampo(campo) {
  if (campo?._key !== undefined && campo._key !== null && campo._key !== "") {
    return String(campo._key);
  }
  return String(campo?.id);
}

/** Valor como o texto que a condicao compara. Exportado para quem compara igual (opcoes). */
export function textoDaCondicao(valor) {
  let texto;
  if (valor === null || valor === undefined) texto = "";
  else if (typeof valor === "boolean") texto = valor ? "true" : "";
  else if (Array.isArray(valor)) texto = valor.map((item) => String(item)).join(", ");
  else texto = String(valor);
  return texto.normalize("NFC").trim().toLowerCase();
}

function objetoSimples(valor) {
  return Object.prototype.toString.call(valor) === "[object Object]";
}

// "Preenchido": booleano vale por si; texto em branco, lista e objeto vazios
// nao contam; numero (inclusive 0) conta.
function estaPreenchido(valor) {
  if (valor === null || valor === undefined) return false;
  if (typeof valor === "boolean") return valor;
  if (typeof valor === "string") return valor.trim() !== "";
  if (Array.isArray(valor)) return valor.length > 0;
  if (objetoSimples(valor)) return Object.keys(valor).length > 0;
  return true;
}

function valorDaReferencia(valores, campo) {
  if (!valores || !Object.prototype.hasOwnProperty.call(valores, String(campo))) {
    return undefined;
  }
  return valores[String(campo)];
}

/** Avalia UMA regra contra o mapa de valores {chave do campo: valor}. */
export function avaliarRegra(regra, valores) {
  if (!regra) return false;
  const atual = valorDaReferencia(valores, regra.campo);

  switch (regra.operador) {
    case "PREENCHIDO":
      return estaPreenchido(atual);
    case "VAZIO":
      return !estaPreenchido(atual);
    case "IGUAL":
    case "DIFERENTE":
    case "CONTEM":
      break;
    default:
      // Operador desconhecido nao satisfaz (fail-safe).
      return false;
  }

  const alvo = textoDaCondicao(regra.valor);
  if (Array.isArray(atual)) {
    const algumItemIgual = atual.some((item) => textoDaCondicao(item) === alvo);
    return regra.operador === "DIFERENTE" ? !algumItemIgual : algumItemIgual;
  }
  const texto = textoDaCondicao(atual);
  if (regra.operador === "IGUAL") return texto === alvo;
  if (regra.operador === "DIFERENTE") return texto !== alvo;
  return texto.includes(alvo);
}

/**
 * A condicao do PROPRIO campo, avaliada contra `valores`. Nao resolve cadeia:
 * para decidir o que aparece use `camposVisiveis`.
 *
 * Sem regras -> visivel. QUALQUER basta uma regra (outro modo exige todas);
 * OCULTAR esconde quando bate (outra acao mostra quando bate).
 */
export function campoVisivel(campo, valores) {
  const cond = campo?.condicao;
  const regras = Array.isArray(cond?.regras) ? cond.regras : [];
  if (regras.length === 0) return true;

  const resultados = regras.map((r) => avaliarRegra(r, valores));
  const bate = cond.modo === "QUALQUER" ? resultados.some(Boolean) : resultados.every(Boolean);
  return cond.acao === "OCULTAR" ? !bate : bate;
}

/**
 * Campos visiveis, na ordem recebida — o algoritmo unico (igual ao backend).
 *
 * Comeca com todos visiveis e percorre os campos NA ORDEM; cada campo e'
 * decidido so com os valores dos campos visiveis naquele momento (valor de
 * campo oculto conta como vazio, e referencia a campo fora da lista tambem).
 * Repete ate uma passada nao mudar nada, no maximo campos.length + 1 vezes:
 * sem ciclo sempre estabiliza; um ciclo antigo termina no limite.
 */
export function camposVisiveis(campos, valores) {
  const lista = Array.isArray(campos) ? campos : [];
  const origem = valores || {};
  const chaves = lista.map(chaveDoCampo);
  const visivel = Object.create(null);
  const atuais = Object.create(null);
  chaves.forEach((chave) => {
    visivel[chave] = true;
    atuais[chave] = origem[chave];
  });

  for (let passada = 0; passada <= lista.length; passada += 1) {
    let mudou = false;
    lista.forEach((campo, indice) => {
      const chave = chaves[indice];
      const agora = campoVisivel(campo, atuais);
      if (agora === visivel[chave]) return;
      visivel[chave] = agora;
      mudou = true;
      if (agora) atuais[chave] = origem[chave];
      else delete atuais[chave];
    });
    if (!mudou) break;
  }

  return lista.filter((_, indice) => visivel[chaves[indice]]);
}
